import { useEffect, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileUp, FolderInput, Pencil, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  filterWorkbooks,
  toWorkbookSummary,
  type WorkbookAccessLevel,
} from "@/features/workbook/mock-data";
import { useWorkbookStore } from "@/stores/workbook";

function accessBadgeVariant(
  accessLevel: WorkbookAccessLevel,
): "info" | "warn" | "neutral" {
  switch (accessLevel) {
    case "Author":
      return "info";
    case "Read Write":
      return "warn";
    default:
      return "neutral";
  }
}

export function WorkbookListPage() {
  const [query, setQuery] = useState("");
  const [uploadingWorkbook, setUploadingWorkbook] = useState(false);
  const [importingHandoff, setImportingHandoff] = useState(false);
  const navigate = useNavigate();
  const load = useWorkbookStore((state) => state.load);
  const importWorkbook = useWorkbookStore((state) => state.importWorkbook);
  const records = useWorkbookStore((state) => state.records);
  const loadStatus = useWorkbookStore((state) => state.status);
  const loadError = useWorkbookStore((state) => state.error);

  const workbookSummaries = useMemo(
    () => records.map(toWorkbookSummary),
    [records],
  );

  useEffect(() => {
    void load();
  }, [load]);

  function onReloadAll() {
    void load().then(() => {
      if (loadError) {
        toast.error("Workbook reload finished with fallback", {
          description: loadError,
        });
        return;
      }
      toast.success("Workbook list refreshed");
    });
  }

  async function onUploadWorkbook(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!file) {
      return;
    }
    setUploadingWorkbook(true);
    try {
      const result = await importWorkbook({
        fileName: file.name,
        importKind: "workbook",
      });
      if (!result) {
        toast.error("Workbook import failed", {
          description: "The backend import task could not be started.",
        });
        return;
      }
      toast.success("Workbook import completed", {
        description: `Task ${result.taskId} created workbook ${result.workbookId}.`,
      });
      navigate(`/workbook/${result.workbookId}/edit?created=1`);
    } finally {
      setUploadingWorkbook(false);
    }
  }

  async function onImportHandoff(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!file) {
      return;
    }
    setImportingHandoff(true);
    try {
      const result = await importWorkbook({
        fileName: file.name,
        importKind: "handoff",
      });
      if (!result) {
        toast.error("Handoff import failed", {
          description: "The backend handoff import task could not be started.",
        });
        return;
      }
      toast.success("Handoff import completed", {
        description: `Task ${result.taskId} created workbook ${result.workbookId}.`,
      });
      navigate(`/workbook/${result.workbookId}/edit?created=1`);
    } finally {
      setImportingHandoff(false);
    }
  }

  const visibleWorkbooks = useMemo(
    () => filterWorkbooks(workbookSummaries, query),
    [query, workbookSummaries],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Workbook
          </h1>
          <p className="mt-1.5 max-w-[760px] text-sm text-fg-dim">
            Mock-first workbook index for a property. This is the new control
            surface for list, create, detail, and edit flows that will absorb
            the legacy Django workbook behavior.
          </p>
        </div>
        <Badge variant="info" size="md">
          {visibleWorkbooks.length} visible
        </Badge>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[260px] flex-1">
            <Search
              size={14}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Filter workbooks for this property"
              className="pl-9"
              aria-label="Filter workbooks"
            />
          </div>
          <Button asChild size="sm">
            <Link to="new">
              <Plus size={14} />
              New workbook
            </Link>
          </Button>
          <label className="inline-flex">
            <input
              type="file"
              className="sr-only"
              accept=".xlsx,.xls,.csv"
              onChange={onUploadWorkbook}
              aria-label="Upload workbook file"
            />
            <Button size="sm" variant="ghost" type="button" asChild>
              <span>
                <FileUp size={14} />
                {uploadingWorkbook ? "Uploading..." : "Upload workbook"}
              </span>
            </Button>
          </label>
          <label className="inline-flex">
            <input
              type="file"
              className="sr-only"
              accept=".xlsx,.xls,.csv"
              onChange={onImportHandoff}
              aria-label="Import handoff file"
            />
            <Button size="sm" variant="ghost" type="button" asChild>
              <span>
                <FolderInput size={14} />
                {importingHandoff ? "Importing..." : "Import handoff"}
              </span>
            </Button>
          </label>
          <Button
            size="sm"
            variant="ghost"
            type="button"
            onClick={() => void onReloadAll()}
            disabled={loadStatus === "loading"}
          >
            {loadStatus === "loading" ? "Refreshing..." : "Reload all data"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <caption className="sr-only">
                Available workbooks for the selected property
              </caption>
              <thead className="bg-surface-2 text-left text-xs uppercase tracking-[0.16em] text-fg-mute">
                <tr>
                  <th className="px-4 py-3 font-semibold">Property</th>
                  <th className="px-4 py-3 font-semibold">Workbook</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Access</th>
                  <th className="px-4 py-3 font-semibold">Author</th>
                  <th className="px-4 py-3 font-semibold">Updated</th>
                  <th className="px-4 py-3 font-semibold">Versions</th>
                  <th className="px-4 py-3 font-semibold text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleWorkbooks.length > 0 ? (
                  visibleWorkbooks.map((workbook) => (
                    <tr
                      key={workbook.id}
                      className="border-t border-line align-top"
                    >
                      <td className="px-4 py-4 text-fg">
                        {workbook.propertyName}
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-semibold text-fg">
                          {workbook.workbookName}
                        </div>
                        <div className="mt-1 max-w-[360px] text-xs text-fg-dim">
                          {workbook.description}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-fg">{workbook.status}</td>
                      <td className="px-4 py-4">
                        <Badge
                          variant={accessBadgeVariant(workbook.accessLevel)}
                          size="sm"
                        >
                          {workbook.accessLevel}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 text-fg">{workbook.author}</td>
                      <td className="px-4 py-4 text-fg-dim">
                        {workbook.updatedAt}
                      </td>
                      <td className="px-4 py-4 text-fg">
                        {workbook.versionCount}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button asChild size="sm" variant="ghost">
                            <Link to={workbook.id}>Details</Link>
                          </Button>
                          <Button asChild size="sm" variant="ghost">
                            <Link to={`${workbook.id}/edit`}>
                              <Pencil size={14} />
                              Edit
                            </Link>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-12 text-center text-fg-dim"
                    >
                      No workbooks match the current filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <CardTitle>Current implementation slice</CardTitle>
            <CardSub>
              Nested routing is now in place. Next steps wire real workbook
              data, access updates, and Handsontable-driven sections.
            </CardSub>
          </div>
          <Button asChild size="sm" variant="ghost">
            <Link to="new">Start new workbook flow</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
